import { useEffect, useState, useRef, useContext, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { styled, vh, darkTheme } from "~/ui/style/stitches.config";
import { TarotPuzzle, PuzzleAnswerCard, DailyPuzzle, getDailyPuzzles, getUnlimitedPuzzles, submitPuzzleResult } from "~/api/gameApi";
import { getTarotCards, TarotCard } from "~/api/communityApi";
import { expandSearchTerms, searchInText } from "~/utils/searchUtils";
import { imageUrl } from "~/utils/imageurl";
import { LoadingIndicator } from "~/pages/loading/LoadingPage";
import { AuthContext } from "~/utils/useAuth";
import { useLocation } from "wouter";
import { HomeNavBar } from "~/ui/home/HomeNavBar";
import { MOONLIGHT_PAGE } from "~/pages/Home";
import { trackEvent } from "~/api/analyticsApi";
import { isIOSNativeApp, isIOSAppVersionAtMost } from "~/utils/deviceDetection";
import { FeatureFeedbackForm } from "~/ui/components/FeatureFeedbackForm";
import { GameHistoryModal } from "./GameHistoryModal";

// ============ Types ============

interface GuessResult {
  card1: TarotCard;
  card2: TarotCard;
  card1Result: "exact" | "partial" | "miss";
  card2Result: "exact" | "partial" | "miss";
}

type GameState = "loading" | "playing" | "won" | "lost";

const MAX_GUESSES = 5;

/** Hardcoded demo puzzle for logged-out users */
const DEMO_PUZZLE: TarotPuzzle = {
  phrase: "The spark of an idea meets the one who has every tool to make it real",
  answer: {
    card_1: { name: "Ace of Wands", suit: "wands", number: 1, arcana: "minor" },
    card_2: { name: "The Magician", suit: "major", number: null, arcana: "major" },
  },
  hints: [
    { level: 1, text: "Something sudden and fiery finding its conduit" },
    { level: 2, text: "A wand's first flame paired with deliberate craft" },
    { level: 3, text: "The very first card of a suit, and the major with all the suits on the table" },
  ],
};

// ============ Helpers ============

/** Parse suit/number from a TarotCard (guidebook data) for comparison */
const parseGuessCard = (card: TarotCard): { suit: string; number: number | null } => {
  const suit = card.category === "major" ? "major" : card.category;
  const numberMap: Record<string, number> = {
    ace: 1, two: 2, three: 3, four: 4, five: 5,
    six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
    page: 11, knight: 12, queen: 13, king: 14,
  };
  const firstWord = card.name.split(" ")[0].toLowerCase();
  const number = numberMap[firstWord] ?? null;
  return { suit, number };
};

/** Compare a guessed card against an answer card */
const compareCard = (
  guess: { suit: string; number: number | null; name: string },
  answer: PuzzleAnswerCard
): "exact" | "partial" | "miss" => {
  if (guess.name === answer.name) return "exact";
  if (guess.suit === answer.suit || (guess.number !== null && guess.number === answer.number))
    return "partial";
  return "miss";
};

/** Get the reset message — "new game at 9pm today" or "new game at 9pm tomorrow" */
const getResetMessage = (): string => {
  const hour = new Date().getHours();
  return hour >= 21 ? "new game at 9pm tomorrow" : "new game at 9pm";
};

// ============ Main Component ============

export const GamePage = () => {
  const { user } = useContext(AuthContext);
  const [, setLocation] = useLocation();
  const [activePage, setActivePage] = useState<MOONLIGHT_PAGE>(MOONLIGHT_PAGE.HOME);
  const [gameState, setGameState] = useState<GameState>("loading");
  const [dailyPuzzles, setDailyPuzzles] = useState<DailyPuzzle[]>([]);
  const [currentPuzzleIndex, setCurrentPuzzleIndex] = useState(0);
  const [puzzle, setPuzzle] = useState<TarotPuzzle | null>(null);
  const [currentPuzzleId, setCurrentPuzzleId] = useState<string | null>(null);
  const [allCards, setAllCards] = useState<TarotCard[]>([]);
  const [guesses, setGuesses] = useState<GuessResult[]>([]);
  const [hintsRevealed, setHintsRevealed] = useState(0);
  const [selectedCard1, setSelectedCard1] = useState<TarotCard | null>(null);
  const [selectedCard2, setSelectedCard2] = useState<TarotCard | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hintUsedThisRound, setHintUsedThisRound] = useState(false);
  const [unlimitedMode, setUnlimitedMode] = useState(false);
  const [hasHistory, setHasHistory] = useState(false);
  const [gameHistoryModalOpen, setGameHistoryModalOpen] = useState(false);

  const pageRef = useRef<HTMLDivElement>(null);
  const hasTrackedVisit = useRef(false);

  // iOS native: hide TopBar header
  useEffect(() => {
    if (isIOSNativeApp()) {
      const topBar = document.querySelector('[data-darkmode]') as HTMLElement;
      if (topBar) {
        topBar.style.display = 'none';
      }

      return () => {
        if (topBar) {
          topBar.style.display = 'flex';
        }
      };
    }
  }, []);

  // Track page visit
  useEffect(() => {
    if (!hasTrackedVisit.current) {
      hasTrackedVisit.current = true;
      trackEvent("/game/visited_page");
    }
  }, []);

  // Prevent body scroll to avoid page jumping
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, []);

  // Load cards + daily puzzles on mount
  useEffect(() => {
    let ignore = false;
    const init = async () => {
      try {
        const cards = await getTarotCards();
        if (!ignore) setAllCards(cards);

        if (!user) {
          // Not logged in — use hardcoded demo puzzle
          if (!ignore) {
            setPuzzle(DEMO_PUZZLE);
            setGameState("playing");
          }
          return;
        }

        const daily = await getDailyPuzzles();
        if (!ignore) {
          setDailyPuzzles(daily.puzzles);
          setHasHistory(!!daily.hasHistory);
          // Find the first uncompleted puzzle
          const firstUncompleted = daily.puzzles.findIndex((p) => !p.completed);
          if (firstUncompleted === -1) {
            // All puzzles completed — show the last daily's result + "Play unlimited" button
            const lastPuzzle = daily.puzzles[daily.puzzles.length - 1];
            setCurrentPuzzleIndex(daily.puzzles.length - 1);
            setPuzzle(lastPuzzle.puzzle);
            setCurrentPuzzleId(lastPuzzle.id);
            setGameState("won");
          } else {
            setCurrentPuzzleIndex(firstUncompleted);
            setPuzzle(daily.puzzles[firstUncompleted].puzzle);
            setCurrentPuzzleId(daily.puzzles[firstUncompleted].id);
            setGameState("playing");
          }
        }
      } catch (err) {
        if (!ignore) {
          console.error("Failed to initialize game:", err);
        }
      }
    };
    init();
    return () => { ignore = true; };
  }, [user]);

  const noMorePuzzles = !dailyPuzzles.some((p, i) => i > currentPuzzleIndex && !p.completed);

  const handleSubmitGuess = useCallback(() => {
    if (!selectedCard1 || !selectedCard2 || !puzzle || isSubmitting) return;
    setIsSubmitting(true);

    const g1 = parseGuessCard(selectedCard1);
    const g2 = parseGuessCard(selectedCard2);

    const resultRank = (r: "exact" | "partial" | "miss") =>
      r === "exact" ? 2 : r === "partial" ? 1 : 0;

    const c1r1 = compareCard({ ...g1, name: selectedCard1.name }, puzzle.answer.card_1);
    const c2r2 = compareCard({ ...g2, name: selectedCard2.name }, puzzle.answer.card_2);
    const scoreA = resultRank(c1r1) + resultRank(c2r2);

    const c1r2 = compareCard({ ...g1, name: selectedCard1.name }, puzzle.answer.card_2);
    const c2r1 = compareCard({ ...g2, name: selectedCard2.name }, puzzle.answer.card_1);
    const scoreB = resultRank(c1r2) + resultRank(c2r1);

    const [card1Result, card2Result] =
      scoreB > scoreA ? [c1r2, c2r1] : [c1r1, c2r2];

    const newGuess: GuessResult = {
      card1: selectedCard1,
      card2: selectedCard2,
      card1Result,
      card2Result,
    };

    const newGuesses = [...guesses, newGuess];
    setGuesses(newGuesses);
    setSelectedCard1(card1Result === "exact" ? selectedCard1 : null);
    setSelectedCard2(card2Result === "exact" ? selectedCard2 : null);
    setHintUsedThisRound(false);

    const won = card1Result === "exact" && card2Result === "exact";
    const lost = newGuesses.length >= MAX_GUESSES && !won;

    if (won || lost) {
      trackEvent("/game/completed_puzzle", {
        result: won ? "won" : "lost",
        guesses: newGuesses.length,
        hints: hintsRevealed,
      });
      // Submit result to server
      if (currentPuzzleId) {
        submitPuzzleResult(currentPuzzleId, won, newGuesses.length)
          .then((res) => {
            // Update the puzzle with full answer data (including card names)
            setPuzzle(res.puzzle);
            setHasHistory(true);
          })
          .catch((err) => console.error("Failed to save result:", err));
      }
      setGameState(won ? "won" : "lost");
      pageRef.current?.scrollTo({ top: 0 });
    }

    setIsSubmitting(false);
  }, [selectedCard1, selectedCard2, puzzle, isSubmitting, guesses, currentPuzzleId, hintsRevealed]);

  const handleRevealHint = () => {
    if (puzzle && hintsRevealed < puzzle.hints.length) {
      setHintsRevealed((prev) => prev + 1);
      setHintUsedThisRound(true);
    }
  };

  const handleNewGame = () => {
    if (unlimitedMode) {
      // In unlimited mode, advance through the unlimited batch
      const nextIndex = dailyPuzzles.findIndex(
        (p, i) => i > currentPuzzleIndex && !p.completed
      );
      if (nextIndex === -1) {
        // Batch done — fetch a new unlimited batch
        handlePlayUnlimited();
        return;
      }
      setCurrentPuzzleIndex(nextIndex);
      setPuzzle(dailyPuzzles[nextIndex].puzzle);
      setCurrentPuzzleId(dailyPuzzles[nextIndex].id);
    } else {
      // Advance to the next uncompleted daily puzzle
      const nextIndex = dailyPuzzles.findIndex(
        (p, i) => i > currentPuzzleIndex && !p.completed
      );
      if (nextIndex === -1) return;
      setCurrentPuzzleIndex(nextIndex);
      setPuzzle(dailyPuzzles[nextIndex].puzzle);
      setCurrentPuzzleId(dailyPuzzles[nextIndex].id);
    }

    setGuesses([]);
    setHintsRevealed(0);
    setSelectedCard1(null);
    setSelectedCard2(null);
    setGameState("playing");
    pageRef.current?.scrollTo({ top: 0 });
  };

  const handlePlayUnlimited = async () => {
    if (!user || (!user.isPlus && !user.isProfessional)) {
      trackEvent("/game/pressed_unlimited_paywall");
      localStorage.setItem('postPaymentRedirect', '/game');
      // Use window.location.href so iOS native app can intercept navigation to show native paywall
      if (isIOSNativeApp()) {
        window.location.href = "/plans";
      } else {
        setLocation("/plans");
      }
      return;
    }
    trackEvent("/game/pressed_unlimited");
    const previousGameState = gameState;
    setGameState("loading");
    try {
      const unlimited = await getUnlimitedPuzzles();
      const unplayedPuzzles = unlimited.puzzles.filter((p) => !p.completed);
      if (unplayedPuzzles.length === 0) {
        setGameState(previousGameState);
        return;
      }
      setDailyPuzzles(unplayedPuzzles);
      setUnlimitedMode(true);
      setCurrentPuzzleIndex(0);
      setPuzzle(unplayedPuzzles[0].puzzle);
      setCurrentPuzzleId(unplayedPuzzles[0].id);
      setGuesses([]);
      setHintsRevealed(0);
      setSelectedCard1(null);
      setSelectedCard2(null);
      setGameState("playing");
      pageRef.current?.scrollTo({ top: 0 });
    } catch (err) {
      console.error("Failed to load unlimited puzzles:", err);
      setGameState(previousGameState);
    }
  };

  return (
    <PageContainer ref={pageRef} isIOSNative={isIOSNativeApp()}>
      <HomeNavBar
        activePage={activePage}
        setActivePage={setActivePage}
        setShouldLongFadeIn={() => {}}
        setIsPayoutsOverlayOpen={() => {}}
      />
      <AnimatePresence mode="wait">
        {gameState === "loading" && (
          <LoadingView key="loading" />
        )}
        {gameState === "playing" && puzzle && (
          <PlayingView
            key="playing"
            puzzle={puzzle}
            allCards={allCards}
            guesses={guesses}
            hintsRevealed={hintsRevealed}
            selectedCard1={selectedCard1}
            selectedCard2={selectedCard2}
            onSelectCard1={setSelectedCard1}
            onSelectCard2={setSelectedCard2}
            onSubmitGuess={handleSubmitGuess}
            onRevealHint={handleRevealHint}
            hintUsedThisRound={hintUsedThisRound}
            puzzleCountLabel={dailyPuzzles.length > 1
              ? (unlimitedMode ? `unlimited play - puzzle ${currentPuzzleIndex + 1} of ${dailyPuzzles.length}` : `daily puzzle ${currentPuzzleIndex + 1} of ${dailyPuzzles.length}`)
              : undefined}
            showHistoryButton={!!user && hasHistory}
            onOpenHistory={() => { trackEvent("/game/history/viewed_page"); setGameHistoryModalOpen(true); }}
          />
        )}
        {(gameState === "won" || gameState === "lost") && puzzle && (
          <ResultView
            key="result"
            gameState={gameState}
            puzzle={puzzle}
            allCards={allCards}
            guesses={guesses}
            onNewGame={!user ? () => setLocation("/signup?dest=/game") : handleNewGame}
            hasMorePuzzles={!user || !noMorePuzzles || unlimitedMode}
            countdown={user && noMorePuzzles && !unlimitedMode ? getResetMessage() : null}
            isDemo={!user}
            puzzleId={currentPuzzleId}
            puzzleCountLabel={dailyPuzzles.length > 1
              ? (unlimitedMode ? `unlimited play - puzzle ${currentPuzzleIndex + 1} of ${dailyPuzzles.length}` : `daily puzzle ${currentPuzzleIndex + 1} of ${dailyPuzzles.length}`)
              : undefined}
            onPlayUnlimited={handlePlayUnlimited}
            showUnlimitedButton={!!(user && noMorePuzzles && !unlimitedMode && !isIOSAppVersionAtMost("1.4.2"))}
            isUnlimitedMode={unlimitedMode}
            noMoreInBatch={noMorePuzzles}
            showHistoryButton={!!user && hasHistory}
            onOpenHistory={() => { trackEvent("/game/history/viewed_page"); setGameHistoryModalOpen(true); }}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {gameHistoryModalOpen && !!user && (
          <GameHistoryModal
            allCards={allCards}
            onClose={() => setGameHistoryModalOpen(false)}
          />
        )}
      </AnimatePresence>
    </PageContainer>
  );
};

// ============ Loading View ============

const LoadingView = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: `calc(${vh(100)} - 120px)` }}
  >
    <LoadingIndicator />
  </motion.div>
);

// ============ Playing View ============

const PlayingView = ({
  puzzle,
  allCards,
  guesses,
  hintsRevealed,
  selectedCard1,
  selectedCard2,
  onSelectCard1,
  onSelectCard2,
  onSubmitGuess,
  onRevealHint,
  hintUsedThisRound,
  puzzleCountLabel,
  showHistoryButton,
  onOpenHistory,
}: {
  puzzle: TarotPuzzle | null;
  allCards: TarotCard[];
  guesses: GuessResult[];
  hintsRevealed: number;
  selectedCard1: TarotCard | null;
  selectedCard2: TarotCard | null;
  onSelectCard1: (c: TarotCard | null) => void;
  onSelectCard2: (c: TarotCard | null) => void;
  onSubmitGuess: () => void;
  onRevealHint: () => void;
  hintUsedThisRound: boolean;
  puzzleCountLabel?: string;
  showHistoryButton?: boolean;
  onOpenHistory?: () => void;
}) => {
  const [showRules, setShowRules] = useState(false);
  const [showKeywords, setShowKeywords] = useState(false);
  const guessGridRef = useRef<HTMLDivElement>(null);
  const prevGuessCount = useRef(guesses.length);

  useEffect(() => {
    if (guesses.length > prevGuessCount.current && guessGridRef.current) {
      guessGridRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    prevGuessCount.current = guesses.length;
  }, [guesses.length]);

  // Cards that got "miss" on both slots are definitively wrong
  const eliminatedCardIds = new Set(
    guesses.flatMap((g) => {
      const ids: string[] = [];
      if (g.card1Result === "miss") ids.push(g.card1.id);
      if (g.card2Result === "miss") ids.push(g.card2.id);
      return ids;
    })
  );

  return (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    style={{ width: "100%", maxWidth: "560px", padding: "36px 32px", boxSizing: "border-box" }}
  >
    <BrandTitle>Pairot</BrandTitle>
    <GameTitle>Guess the combo</GameTitle>
    <TopButtonRow>
      <HowToPlayButton onClick={() => setShowRules(true)}>How to play</HowToPlayButton>
      {showHistoryButton && onOpenHistory && (
        <HowToPlayButton onClick={onOpenHistory}>Past puzzles</HowToPlayButton>
      )}
    </TopButtonRow>
    {puzzleCountLabel && <GuessesLeftLabel>{puzzleCountLabel}</GuessesLeftLabel>}
    <PhraseDisplay>{puzzle?.phrase}</PhraseDisplay>

    {/* Hint area */}
    {puzzle && puzzle.hints.length > 0 && (hintsRevealed > 0 || guesses.length > 0) && (
      <HintArea>
        {puzzle.hints.slice(0, hintsRevealed).map((hint, i) => (
          <motion.div key={`hint-${i}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <HintText>{hint.text}</HintText>
          </motion.div>
        ))}
        {hintsRevealed < puzzle.hints.length && guesses.length > 0 && !hintUsedThisRound && (
          <motion.div key={`hint-link-${hintsRevealed}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <HintLink onClick={onRevealHint}>
              {hintsRevealed === 0 ? "get hint" : "next hint"}
            </HintLink>
          </motion.div>
        )}
      </HintArea>
    )}

    {/* Card selectors + submit arrow */}
    <CardSlotsRow>
      <CardSelector
        cards={allCards}
        selected={selectedCard1}
        onSelect={onSelectCard1}
        disabledCardId={selectedCard2?.id ?? null}
        eliminatedCardIds={eliminatedCardIds}
        showKeywords={showKeywords}
      />
      <CardSelector
        cards={allCards}
        selected={selectedCard2}
        onSelect={onSelectCard2}
        disabledCardId={selectedCard1?.id ?? null}
        eliminatedCardIds={eliminatedCardIds}
        showKeywords={showKeywords}
      />
      <AnimatePresence>
        {selectedCard1 && selectedCard2 && (
          <SubmitArrowWrapper
            key="submit-arrow"
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.7 }}
          >
            <SubmitArrowButton onClick={onSubmitGuess}>
              <img src="/images/back-nav-icon.svg" alt="Submit guess" />
            </SubmitArrowButton>
          </SubmitArrowWrapper>
        )}
      </AnimatePresence>
    </CardSlotsRow>
    {(selectedCard1 || selectedCard2) && (
      <div style={{ textAlign: "center", marginBottom: "16px" }}>
        <ShowKeywordsToggle onClick={() => setShowKeywords(!showKeywords)}>
          {showKeywords ? "hide keywords" : "show keywords"}
        </ShowKeywordsToggle>
      </div>
    )}

    {/* Guess grid */}
    <div ref={guessGridRef}>
    <GuessesLeftLabel>{MAX_GUESSES - guesses.length} guesses left</GuessesLeftLabel>
    <GuessHistory>
      {Array.from({ length: MAX_GUESSES }).map((_, i) => {
        const guess = guesses[i];
        return guess ? (
          <GuessRow key={i}>
            <GuessChip variant={guess.card1Result}>
              {guess.card1.imagePath && (
                <GuessCardImage src={imageUrl(guess.card1.imagePath, 60)} alt="" />
              )}
              <GuessCardName>{guess.card1.name}</GuessCardName>
            </GuessChip>
            <GuessChip variant={guess.card2Result}>
              {guess.card2.imagePath && (
                <GuessCardImage src={imageUrl(guess.card2.imagePath, 60)} alt="" />
              )}
              <GuessCardName>{guess.card2.name}</GuessCardName>
            </GuessChip>
          </GuessRow>
        ) : (
          <GuessRow key={i}>
            <EmptyGuessChip />
            <EmptyGuessChip />
          </GuessRow>
        );
      })}
    </GuessHistory>
    </div>

    {/* How to Play modal */}
    <AnimatePresence>
      {showRules && (
        <>
          <RulesOverlay
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowRules(false)}
          />
          <RulesModal
            as={motion.div}
            isIOSNative={isIOSNativeApp()}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <RulesCard>
              <RulesTitle>How to play</RulesTitle>
              <RulesText>You'll see a phrase inspired by two tarot cards. Your goal is to guess which card combo it refers to. (We know meanings are subjective! This is just an exercise.) </RulesText>
              <RulesText>Select your two cards and submit your guess. You have <strong>5 guesses</strong> and 3 hints.</RulesText>
              <RulesText>After each guess, see if you got any cards right:</RulesText>
              <RulesList>
                <li><RulesDot variant="exact" /> Exact match</li>
                <li><RulesDot variant="partial" /> Right suit OR right number, wrong card</li>
                <li><RulesDot variant="miss" /> No match</li>
              </RulesList>
              <RulesText>Order doesn't matter. Just find the right pair!</RulesText>
              <RulesCloseButton onClick={() => setShowRules(false)}>Got it</RulesCloseButton>
            </RulesCard>
          </RulesModal>
        </>
      )}
    </AnimatePresence>
  </motion.div>
  );
};

// ============ Result View ============

const ResultView = ({
  gameState,
  puzzle,
  allCards,
  guesses,
  onNewGame,
  hasMorePuzzles,
  countdown,
  isDemo,
  puzzleId,
  puzzleCountLabel,
  onPlayUnlimited,
  showUnlimitedButton,
  isUnlimitedMode,
  noMoreInBatch,
  showHistoryButton,
  onOpenHistory,
}: {
  gameState: "won" | "lost";
  puzzle: TarotPuzzle;
  allCards: TarotCard[];
  guesses: GuessResult[];
  onNewGame: () => void;
  hasMorePuzzles: boolean;
  countdown: string | null;
  isDemo?: boolean;
  puzzleId?: string | null;
  puzzleCountLabel?: string;
  onPlayUnlimited?: () => void;
  showUnlimitedButton?: boolean;
  isUnlimitedMode?: boolean;
  noMoreInBatch?: boolean;
  showHistoryButton?: boolean;
  onOpenHistory?: () => void;
}) => {
  const isLost = gameState === "lost";
  const [copied, setCopied] = useState(false);
  const [showKeywords, setShowKeywords] = useState(false);
  const answerCard1 = allCards.find((c) => c.name === puzzle.answer.card_1.name);
  const answerCard2 = allCards.find((c) => c.name === puzzle.answer.card_2.name);

  return (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0 }}
    style={{ textAlign: "center", width: "100%", maxWidth: "560px", padding: "36px 32px", boxSizing: "border-box" }}
  >
    <BrandTitle>Pairot</BrandTitle>
    <GameTitle>{guesses.length === 0 ? "Guess the combo" : isLost ? "Almost!" : "You got it!"}</GameTitle>
    {showHistoryButton && onOpenHistory && (
      <TopButtonRow>
        <HowToPlayButton onClick={onOpenHistory}>Past puzzles</HowToPlayButton>
      </TopButtonRow>
    )}
    {puzzleCountLabel && <GuessesLeftLabel>{puzzleCountLabel}</GuessesLeftLabel>}

    {/* Clue */}
    <PhraseDisplay>{puzzle.phrase}</PhraseDisplay>

    {/* Answer cards */}
    <AnswerLabel>Our answer was:</AnswerLabel>
    <CardSlotsRow>
      <SelectorWrapper>
        <CardSlotFilled>
          {answerCard1?.imagePath && (
            <CardSlotImage src={imageUrl(answerCard1.imagePath, 200)} alt={puzzle.answer.card_1.name} />
          )}
        </CardSlotFilled>
        <CardSlotLabel>{puzzle.answer.card_1.name}</CardSlotLabel>
        {showKeywords && answerCard1?.keywords && answerCard1.keywords.length > 0 && (
          <AnswerKeywords>{answerCard1.keywords.join(" • ")}</AnswerKeywords>
        )}
      </SelectorWrapper>
      <SelectorWrapper>
        <CardSlotFilled>
          {answerCard2?.imagePath && (
            <CardSlotImage src={imageUrl(answerCard2.imagePath, 200)} alt={puzzle.answer.card_2.name} />
          )}
        </CardSlotFilled>
        <CardSlotLabel>{puzzle.answer.card_2.name}</CardSlotLabel>
        {showKeywords && answerCard2?.keywords && answerCard2.keywords.length > 0 && (
          <AnswerKeywords>{answerCard2.keywords.join(" • ")}</AnswerKeywords>
        )}
      </SelectorWrapper>
    </CardSlotsRow>
    <ShowKeywordsToggle onClick={() => setShowKeywords(!showKeywords)}>
      {showKeywords ? "hide keywords" : "show keywords"}
    </ShowKeywordsToggle>
    {guesses.length > 0 && (
      <InterpretationNote>
        {isLost ? "There's many ways to read the cards." : "Nice! Tarot is subjective, but we're on the same wavelength."}
      </InterpretationNote>
    )}

    {/* Action buttons */}
    <ShareRow css={{ marginTop: "20px", marginBottom: "28px" }}>
      {hasMorePuzzles && (
        <GameButton onClick={onNewGame}>
          {isDemo ? "New puzzle" : (isUnlimitedMode && noMoreInBatch ? "New game" : "New puzzle")}
        </GameButton>
      )}
    </ShareRow>
    {countdown && (
      <CountdownPill css={{ marginBottom: showUnlimitedButton ? "16px" : (guesses.length > 0 ? "26px" : "0") }}>
        <CountdownDot />
        {countdown}
      </CountdownPill>
    )}
    {showUnlimitedButton && onPlayUnlimited && (
      <GameButton
        onClick={onPlayUnlimited}
        css={{ marginBottom: guesses.length > 0 ? "26px" : "0" }}
      >
        Play unlimited
      </GameButton>
    )}

    {/* Guess history */}
    {guesses.length > 0 && (
      <>
      <GuessHistory>
        {guesses.map((guess, i) => (
          <GuessRow key={i}>
            <GuessChip variant={guess.card1Result}>
              {guess.card1.imagePath && (
                <GuessCardImage src={imageUrl(guess.card1.imagePath, 60)} alt="" />
              )}
              <GuessCardName>{guess.card1.name}</GuessCardName>
            </GuessChip>
            <GuessChip variant={guess.card2Result}>
              {guess.card2.imagePath && (
                <GuessCardImage src={imageUrl(guess.card2.imagePath, 60)} alt="" />
              )}
              <GuessCardName>{guess.card2.name}</GuessCardName>
            </GuessChip>
          </GuessRow>
        ))}
      </GuessHistory>
      {!isDemo && !isUnlimitedMode && (
        <GameButton css={{ marginTop: "4px", minWidth: "130px" }} onClick={() => {
          const resultIconMap = { exact: "🟢", partial: "🟡", miss: "⚪" };
          const lines = guesses.map(
            (g) => `${resultIconMap[g.card1Result]} ${resultIconMap[g.card2Result]}`
          );
          const summary = isLost ? "" : `Got it in ${guesses.length}/${MAX_GUESSES} guesses`;
          const text = `I played the tarot combo puzzle on Moonlight Tarot\n"${puzzle.phrase}"\n${lines.join("\n")}\n${summary}\nhttps://app.moonlight.world/game`;
          navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }}>
          {copied ? "Copied!" : "Copy results"}
        </GameButton>
      )}
      </>
    )}

    {/* Feedback form */}
    {!isDemo && (
      <FeatureFeedbackForm
        formId="mB6KVA"
        description="Our daily puzzle game is new! Let us know what you think."
        feature="game"
        spread={puzzleId || undefined}
        style={{ border: "1px solid rgba(0, 0, 0, 0.3)", borderRadius: "1.2rem", marginTop: "30px", width: "90%", marginLeft: "auto", marginRight: "auto" }}
      />
    )}
  </motion.div>
  );
};

// ============ Card Selector ============

const SUIT_CATEGORIES = ["major", "wands", "cups", "swords", "pentacles"] as const;

const suitLabel = (category: string) => {
  const labels: Record<string, string> = {
    major: "Major Arcana",
    cups: "Cups",
    pentacles: "Pentacles",
    swords: "Swords",
    wands: "Wands",
  };
  return labels[category] || category;
};

const CardSelector = ({
  cards,
  selected,
  onSelect,
  disabledCardId,
  eliminatedCardIds,
  showKeywords,
}: {
  cards: TarotCard[];
  selected: TarotCard | null;
  onSelect: (c: TarotCard | null) => void;
  disabledCardId: string | null;
  eliminatedCardIds: Set<string>;
  showKeywords?: boolean;
}) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const isSearching = query.length > 0;

  const searchResults = cards.filter((c) => {
    if (!query) return false;
    const expandedTerms = expandSearchTerms(query);
    return searchInText(expandedTerms, c.name);
  });

  const categoryCards = activeCategory
    ? cards.filter((c) => c.category === activeCategory)
    : [];

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setActiveCategory(null);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const selectCard = (card: TarotCard) => {
    onSelect(card);
    setQuery("");
    setIsOpen(false);
    setActiveCategory(null);
  };

  const renderCardRow = (card: TarotCard) => {
    const isEliminated = eliminatedCardIds.has(card.id);
    const isDisabled = card.id === disabledCardId || isEliminated;
    return (
      <DropdownItem
        key={card.id}
        onClick={isDisabled ? undefined : () => selectCard(card)}
        disabled={isDisabled}
        variant={isDisabled ? "disabled" : undefined}
      >
        {card.imagePath && (
          <DropdownCardImage src={imageUrl(card.imagePath, 40)} alt="" />
        )}
        <DropdownCardInfo>
          <DropdownCardName>{card.name}</DropdownCardName>
        </DropdownCardInfo>
      </DropdownItem>
    );
  };

  return (
    <SelectorWrapper ref={wrapperRef}>
      {selected ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.35 }}>
        <CardSlotFilledWrapper>
          <CardSlotFilled>
            {selected.imagePath && (
              <CardSlotImage src={imageUrl(selected.imagePath, 200)} alt={selected.name} />
            )}
          </CardSlotFilled>
          <RemoveCardButton onClick={() => {
            setQuery("");
            onSelect(null);
            setActiveCategory(null);
          }}>×</RemoveCardButton>
        </CardSlotFilledWrapper>
        </motion.div>
      ) : (
        <CardSlotEmpty onClick={() => setIsOpen(!isOpen)}>
          <CardSlotPlus>+</CardSlotPlus>
        </CardSlotEmpty>
      )}
      <CardSlotLabel>{selected ? selected.name : ""}</CardSlotLabel>
      {showKeywords && selected?.keywords && selected.keywords.length > 0 && (
        <AnswerKeywords>{selected.keywords.join(" • ")}</AnswerKeywords>
      )}
      {isOpen && !selected && (
        <>
        <DropdownOverlay onClick={() => {
          setIsOpen(false);
          setActiveCategory(null);
        }} />
        <DropdownList>
          <DropdownSearchInput
            type="text"
            placeholder="Search cards..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveCategory(null);
            }}
            autoFocus
          />
          {isSearching ? (
            <>
              {searchResults.map(renderCardRow)}
              {searchResults.length === 0 && (
                <DropdownMore>No cards found</DropdownMore>
              )}
            </>
          ) : activeCategory ? (
            <>
              <DropdownItem onClick={() => setActiveCategory(null)}>
                <DropdownBackArrow>←</DropdownBackArrow>
                <DropdownCardName>{suitLabel(activeCategory)}</DropdownCardName>
              </DropdownItem>
              {categoryCards.map(renderCardRow)}
            </>
          ) : (
            SUIT_CATEGORIES.map((cat) => (
              <DropdownItem key={cat} onClick={() => setActiveCategory(cat)}>
                <DropdownCardName>{suitLabel(cat)}</DropdownCardName>
                <DropdownChevron>›</DropdownChevron>
              </DropdownItem>
            ))
          )}
        </DropdownList>
        </>
      )}
    </SelectorWrapper>
  );
};

// ============ Layout Styled Components ============

const PageContainer = styled("div", {
  height: vh(100),
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  paddingTop: "80px",
  paddingBottom: "140px",
  paddingLeft: "$navWidth",
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  backgroundColor: "$appBg",
  overflowY: "auto",
  position: "relative",
  boxSizing: "border-box",
  // same scrollbar as the rest of the app (see index.css)
  "&::-webkit-scrollbar": { width: "0.5rem", height: "0.5rem" },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "$grayA200",
    borderRadius: "0.5rem",
    [`.${darkTheme.className} &`]: { backgroundColor: "$whiteA500" },
  },
  "@mobile": {
    paddingLeft: 0,
    paddingBottom: "260px",
  },
  variants: {
    isIOSNative: {
      true: {
        paddingTop: "40px",
        "@notmobile": {
          paddingLeft: 0,
        },
      },
    },
  },
});

// ============ Typography ============

const BrandTitle = styled("h2", {
  textAlign: "center",
  fontFamily: "KeplerLightCondensed",
  fontSize: "27px",
  fontWeight: 400,
  letterSpacing: "-1px",
  textTransform: "uppercase",
  color: "$text",
  marginBottom: "8px",
  marginTop: "-8px",
});

const GameTitle = styled("h1", {
  textAlign: "center",
  fontFamily: "GorgeousBaby",
  fontSize: "48px",
  fontWeight: 400,
  lineHeight: 1.1,
  color: "$text",
  marginBottom: "12px",
});

const InterpretationNote = styled("p", {
  textAlign: "center",
  fontFamily: "WorkSans",
  width: "300px",
  lineHeight: "1.3",
  fontSize: "12px",
  fontWeight: 400,
  color: "$gray800",
  [`.${darkTheme.className} &`]: { color: "$textMuted" },
  margin: "14px auto 0",
});

const AnswerLabel = styled("p", {
  textAlign: "center",
  fontSize: "11px",
  textTransform: "uppercase",
  fontWeight: 400,
  color: "$gray500",
  [`.${darkTheme.className} &`]: { color: "$textMuted" },
  margin: "0 0 16px",
});

const PhraseDisplay = styled("p", {
  textAlign: "center",
  textWrap: "balance",
  fontSize: "16px",
  fontWeight: 400,
  lineHeight: 1.3,
  color: "$text",
  marginBottom: "24px",
});

const CountdownPill = styled("div", {
  fontSize: "11px",
  color: "$text",
  paddingBottom: "2px",
  borderRadius: "100px",
  textTransform: "uppercase",
  fontVariantNumeric: "tabular-nums",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
  whiteSpace: "nowrap",
  backgroundColor: "transparent",
  width: "fit-content",
  margin: "0 auto",
});

const CountdownDot = styled("div", {
  width: "5px",
  height: "5px",
  borderRadius: "50%",
  backgroundColor: "$text",
});

// ============ Guess History ============

const GuessHistory = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "10px",
  marginBottom: "24px",
});

const GuessRow = styled("div", {
  display: "flex",
  gap: "10px",
  justifyContent: "center",
});

const GuessChip = styled("div", {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: "8px",
  padding: "6px 10px",
  borderRadius: "10px",
  width: "160px",
  height: "56px",
  boxSizing: "border-box",
  variants: {
    variant: {
      exact: {
        background: "#DAEEA1",
      },
      partial: {
        background: "#fde68a",
      },
      miss: {
        background: "$gray200",
        [`.${darkTheme.className} &`]: {
          background: "$subtleFill",
          "& span": { color: "$text" },
        },
      },
    },
  },
});

const EmptyGuessChip = styled("div", {
  width: "160px",
  height: "56px",
  borderRadius: "10px",
  background: "$surface",
  boxSizing: "border-box",
});

const GuessesLeftLabel = styled("p", {
  textAlign: "center",
  fontSize: "11px",
  fontWeight: 400,
  color: "$gray500",
  [`.${darkTheme.className} &`]: { color: "$textMuted" },
  marginBottom: "8px",
  textTransform: "uppercase",
});

const GuessCardImage = styled("img", {
  width: "28px",
  height: "44px",
  objectFit: "cover",
  borderRadius: "4px",
  flexShrink: 0,
});

const GuessCardName = styled("span", {
  fontSize: "11px",
  fontWeight: 500,
  color: "#000",
  lineHeight: "1.3",
  minWidth: 0,
});

// ============ Card Selector Styles ============

const SelectorWrapper = styled("div", {
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "100px",
});

const CardSlotEmpty = styled("button", {
  width: "90px",
  height: "141px",
  borderRadius: "12px",
  border: "1px dashed $gray600",
  background: "$surface",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.2s",
  [`.${darkTheme.className} &`]: { borderColor: "$textMuted" },
  "&:hover": {
    borderColor: "$border",
    background: "$inputBg",
  },
});

const CardSlotPlus = styled("span", {
  fontSize: "22px",
  color: "$gray800",
  [`.${darkTheme.className} &`]: { color: "$text" },
  fontWeight: 300,
});

const CardSlotFilledWrapper = styled("div", {
  position: "relative",
});

const CardSlotFilled = styled("div", {
  width: "90px",
  height: "141px",
  borderRadius: "10px",
  border: "1px solid $gray300",
  [`.${darkTheme.className} &`]: { borderColor: "$inputBorder" },
  background: "transparent",
  padding: 0,
  overflow: "hidden",
});

const RemoveCardButton = styled("button", {
  position: "absolute",
  top: "-6px",
  right: "-6px",
  width: "18px",
  height: "18px",
  borderRadius: "50%",
  backgroundColor: "$inverseSurface",
  color: "$inverseText",
  border: "none",
  fontSize: "13px",
  fontWeight: "200",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&:hover": {
    opacity: 0.7,
  },
});

const CardSlotImage = styled("img", {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  borderRadius: "10px",
});

const CardSlotLabel = styled("div", {
  marginTop: "6px",
  fontSize: "12px",
  fontWeight: 500,
  color: "$gray800",
  [`.${darkTheme.className} &`]: { color: "$text" },
  textAlign: "center",
  height: "calc(2 * 1.3em)",
  maxWidth: "100px",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  textOverflow: "ellipsis",
  lineHeight: 1.3,
});

export const ShowKeywordsToggle = styled("button", {
  background: "rgba(0, 0, 0, 0.05)",
  border: "none",
  borderRadius: "20px",
  padding: "5px 12px",
  fontSize: "10px",
  fontWeight: 400,
  color: "$gray500",
  cursor: "pointer",
  marginTop: "10px",
  letterSpacing: "0.02em",
  transition: "background 0.2s ease",
  [`.${darkTheme.className} &`]: {
    background: "rgba(255, 255, 255, 0.1)",
    color: "$textMuted",
  },
  "&:hover": {
    background: "rgba(0, 0, 0, 0.08)",
    [`.${darkTheme.className} &`]: { background: "rgba(255, 255, 255, 0.18)" },
  },
});

export const AnswerKeywords = styled("div", {
  fontSize: "9px",
  fontWeight: 300,
  lineHeight: "0.9rem",
  marginTop: "4px",
  textTransform: "lowercase",
  textAlign: "center",
  color: "$gray600",
  [`.${darkTheme.className} &`]: { color: "$textMuted" },
  letterSpacing: "0.01em",
  maxWidth: "120px",
});

const CardSlotsRow = styled("div", {
  position: "relative",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  gap: "12px",
  marginBottom: "16px",
});


const DropdownOverlay = styled("div", {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: "rgba(180, 180, 180, 0.4)",
  [`.${darkTheme.className} &`]: { background: "$scrim" },
  zIndex: 10001,
});

const DropdownList = styled("div", {
  position: "absolute",
  top: "0",
  left: "50%",
  transform: "translateX(-50%)",
  width: "220px",
  maxHeight: "240px",
  overflowY: "auto",
  borderRadius: "16px",
  background: "$surface",
  border: "1px solid $border",
  boxShadow: "0 2px 0 1px $grayA200",
  zIndex: 10002,
  paddingTop: "4px",
  paddingBottom: "8px",
  "&::-webkit-scrollbar": { width: "6px" },
  "&::-webkit-scrollbar-thumb": {
    borderRadius: "6px",
    backgroundColor: "rgba(127, 127, 127, .5)",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent",
    marginTop: "12px",
    marginBottom: "12px",
  },
});

const DropdownSearchInput = styled("input", {
  width: "100%",
  padding: "10px 12px",
  border: "none",
  borderBottom: "1px solid $gray200",
  [`.${darkTheme.className} &`]: { borderBottomColor: "$inputBorder" },
  background: "transparent",
  fontSize: "13px",
  color: "$text",
  outline: "none",
  boxSizing: "border-box",
  "&::placeholder": {
    color: "$gray400",
    [`.${darkTheme.className} &`]: { color: "$textMuted" },
  },
});

const DropdownItem = styled("button", {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  width: "100%",
  padding: "8px 12px",
  border: "none",
  background: "transparent",
  textAlign: "left",
  fontSize: "13px",
  color: "$text",
  cursor: "pointer",
  transition: "background 0.15s",
  "&:hover": {
    background: "$inputBg",
  },
  "&:not(:last-child)": {
    borderBottom: "1px solid $gray200",
    [`.${darkTheme.className} &`]: { borderBottomColor: "$inputBorder" },
  },
  variants: {
    variant: {
      disabled: {
        opacity: 0.35,
        cursor: "default",
        "&:hover": {
          background: "transparent",
        },
      },
    },
  },
});

const DropdownCardImage = styled("img", {
  width: "28px",
  height: "44px",
  objectFit: "cover",
  borderRadius: "3px",
  flexShrink: 0,
});

const DropdownCardInfo = styled("div", {
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
});

const DropdownCardName = styled("span", {
  fontSize: "13px",
  fontWeight: 400,
  color: "$text",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const DropdownBackArrow = styled("span", {
  fontSize: "16px",
  color: "$text",
  marginRight: "2px",
});

const DropdownChevron = styled("span", {
  marginLeft: "auto",
  fontSize: "18px",
  color: "$text",
  lineHeight: 1,
  fontWeight: 200,
});

const DropdownMore = styled("div", {
  padding: "14px 14px",
  fontSize: "12px",
  color: "$gray500",
  [`.${darkTheme.className} &`]: { color: "$textMuted" },
  textAlign: "center",
});

// ============ Buttons ============

const SubmitArrowWrapper = styled(motion.div, {
  position: "absolute",
  right: "20px",
  top: "0",
  height: "141px",
  display: "flex",
  alignItems: "center",
  "@mobile": {
    right: "-12px",
  },
});

const SubmitArrowButton = styled("button", {
  width: "48px",
  height: "48px",
  borderRadius: "50%",
  backgroundColor: "$inverseSurface",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  cursor: "pointer",
  border: "none",
  transition: "transform 0.2s",
  "&:hover": {
    transform: "scale(1.1)",
  },
  "& img": {
    width: "50%",
    filter: "invert(1)",
    [`.${darkTheme.className} &`]: { filter: "none" },
    transform: "rotate(180deg)",
  },
});

const GameButton = styled("button", {
  padding: "10px 24px",
  borderRadius: "30px",
  border: "1px solid $border",
  background: "$surface",
  color: "$text",
  fontSize: "13px",
  fontWeight: 400,
  cursor: "pointer",
  transition: "background 0.2s",
  "&:hover": {
    background: "#f0f0f0",
    [`.${darkTheme.className} &`]: { background: "$subtleFill" },
  },
  "&:disabled": {
    opacity: 0.35,
    cursor: "not-allowed",
  },
});

// ============ Hints ============

const HintArea = styled("div", {
  minHeight: "24px",
  marginTop: "-16px",
  marginBottom: "16px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "4px",
});

const HintLink = styled("button", {
  display: "block",
  background: "none",
  border: "none",
  padding: 0,
  fontSize: "12px",
  color: "$text",
  textDecoration: "underline",
  cursor: "pointer",
  "&:hover": {
    color: "$text",
  },
});

const HintText = styled("p", {
  fontSize: "13px",
  color: "$gray400",
  [`.${darkTheme.className} &`]: { color: "$textMuted" },
  fontStyle: "italic",
  lineHeight: 1.2,
  marginBottom: "4px",
  textAlign: "center",
  textWrap: "balance",
});

// ============ Result Screen ============

const ShareRow = styled("div", {
  display: "flex",
  gap: "10px",
  justifyContent: "center",
});

// ============ How to Play Modal ============

const HowToPlayButton = styled("button", {
  minWidth: "100px",
  padding: "8px 16px",
  borderRadius: "20px",
  border: "none",
  background: "$gray200",
  color: "$text",
  [`.${darkTheme.className} &`]: { background: "$subtleFill" },
  fontSize: "12px",
  fontWeight: 400,
  cursor: "pointer",
  transition: "background 0.2s",
  "&:hover": {
    background: "$gray300",
    [`.${darkTheme.className} &`]: { background: "$inputBorder" },
  },
});

const TopButtonRow = styled("div", {
  display: "flex",
  justifyContent: "center",
  gap: "8px",
  margin: "-6px 0 20px",
});

export const RulesOverlay = styled("div", {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: "rgba(180, 180, 180, 0.4)",
  [`.${darkTheme.className} &`]: { background: "$scrim" },
  zIndex: 10001,
});

export const RulesModal = styled("div", {
  position: "fixed",
  top: 0,
  left: "$navWidth",
  right: 0,
  bottom: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 10002,
  "@mobile": {
    left: 0,
  },
  pointerEvents: "none",
  "& > *": {
    pointerEvents: "auto",
  },
  variants: {
    isIOSNative: {
      true: {
        "@notmobile": {
          left: 0,
        },
      },
    },
  },
});

const RulesCard = styled("div", {
  width: "90%",
  maxWidth: "360px",
  background: "$surface",
  borderRadius: "24px",
  padding: "34px 34px",
  border: "1px solid $border",
});

const RulesTitle = styled("h2", {
  fontFamily: "KeplerLightCondensed",
  fontSize: "28px",
  fontWeight: 400,
  textAlign: "left",
  marginBottom: "16px",
  color: "$text",
});

const RulesText = styled("p", {
  fontFamily: "WorkSans",
  fontSize: "13px",
  fontWeight: 400,
  lineHeight: 1.6,
  color: "#333",
  [`.${darkTheme.className} &`]: { color: "$text" },
  marginBottom: "8px",
  textAlign: "left",
  "& strong": {
    fontWeight: 500,
  },
});

const RulesList = styled("ul", {
  listStyle: "none",
  padding: 0,
  margin: "8px 0 12px",
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  alignItems: "flex-start",
  fontFamily: "WorkSans",
  fontSize: "13px",
  fontWeight: 400,
  color: "#333",
  [`.${darkTheme.className} &`]: { color: "$text" },
  "& li": {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
});

const RulesDot = styled("span", {
  display: "inline-block",
  width: "12px",
  height: "12px",
  borderRadius: "50%",
  flexShrink: 0,
  variants: {
    variant: {
      exact: { background: "#DAEEA1" },
      partial: { background: "#fde68a" },
      miss: {
        background: "$gray200",
        [`.${darkTheme.className} &`]: { background: "$subtleFill" },
      },
    },
  },
});

const RulesCloseButton = styled("button", {
  display: "block",
  margin: "16px auto 0",
  padding: "9px 30px",
  borderRadius: "12px",
  border: "1px solid $border",
  background: "$surface",
  color: "$text",
  fontSize: "13px",
  fontWeight: 400,
  cursor: "pointer",
  transition: "background 0.2s",
  "&:hover": {
    background: "$inputBg",
  },
});
